const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;

export function rangeHighLabel(period) {
    return period === '1Y' ? '52-week high' : 'Period high';
}

export function rangeLowLabel(period) {
    return period === '1Y' ? '52-week low' : 'Period low';
}

export function formatVolume(v) {
    const n = Number(v);
    if (!Number.isFinite(n)) return '—';
    const rounded = Math.round(n);
    if (Math.abs(rounded) >= 1e7) {
        return `${(rounded / 1e7).toLocaleString('en-IN', { maximumFractionDigits: 2 })} Cr`;
    }
    if (Math.abs(rounded) >= 1e5) {
        return `${(rounded / 1e5).toLocaleString('en-IN', { maximumFractionDigits: 2 })} L`;
    }
    return rounded.toLocaleString('en-IN');
}

export function rangeBias(rangePosPct) {
    if (!Number.isFinite(rangePosPct)) return null;
    if (rangePosPct >= 80) return 'Near the high';
    if (rangePosPct <= 20) return 'Near the low';
    return 'Mid-range';
}

function finiteOrClose(value, close) {
    const n = Number(value);
    return Number.isFinite(n) ? n : Number(close);
}

/**
 * Investor-facing stats from a daily OHLCV window.
 * No technical indicators — range, drawdown, compounding, and liquidity.
 */
export function computePriceStats(rows, options = {}) {
    const { period, currentPrice, sharesOutstanding } = options;
    const withClose = (Array.isArray(rows) ? rows : []).filter((d) =>
        Number.isFinite(Number(d?.close))
    );
    if (!withClose.length) return null;

    const closes = withClose.map((d) => Number(d.close));
    const highs = withClose.map((d) => finiteOrClose(d.high, d.close));
    const lows = withClose.map((d) => finiteOrClose(d.low, d.close));
    const volumes = withClose.map((d) => Number(d.volume)).filter(Number.isFinite);

    const first = closes[0];
    const last = closes[closes.length - 1];
    const prev = closes.length > 1 ? closes[closes.length - 2] : null;
    const dayChange = prev != null ? last - prev : null;
    const dayChangePct = prev ? (dayChange / prev) * 100 : null;

    const periodChange = last - first;
    const periodChangePct = first ? (periodChange / first) * 100 : null;

    const periodHigh = Math.max(...highs);
    const periodLow = Math.min(...lows);
    const avgClose = closes.reduce((sum, n) => sum + n, 0) / closes.length;

    const rangePosPct =
        periodHigh !== periodLow
            ? ((last - periodLow) / (periodHigh - periodLow)) * 100
            : 50;
    const fromHighPct = periodHigh ? ((last - periodHigh) / periodHigh) * 100 : null;

    let peak = closes[0];
    let maxDd = 0;
    for (const close of closes) {
        if (close > peak) peak = close;
        const dd = peak ? (close - peak) / peak : 0;
        if (dd < maxDd) maxDd = dd;
    }

    const start = new Date(withClose[0].date);
    const end = new Date(withClose[withClose.length - 1].date);
    const years =
        Number.isFinite(start.getTime()) && Number.isFinite(end.getTime())
            ? (end.getTime() - start.getTime()) / MS_PER_YEAR
            : null;
    const showCagr = period === '1Y' || period === '5Y' || period === 'MAX';
    const cagr =
        showCagr && years != null && years >= 0.75 && first > 0
            ? ((last / first) ** (1 / years) - 1) * 100
            : null;

    const avgVolume = volumes.length
        ? volumes.reduce((sum, n) => sum + n, 0) / volumes.length
        : null;
    const lastVolume = volumes.length ? volumes[volumes.length - 1] : null;

    const shares = Number(sharesOutstanding);
    const hasShares = Number.isFinite(shares) && shares > 0;

    const cmp = Number.isFinite(Number(currentPrice)) ? Number(currentPrice) : last;

    return {
        first,
        last,
        cmp,
        dayChange,
        dayChangePct,
        periodChange,
        periodChangePct,
        periodHigh,
        periodLow,
        avgClose,
        rangePosPct,
        fromHighPct,
        maxDrawdownPct: maxDd * 100,
        cagr,
        years,
        avgVolume,
        lastVolume,
        mcapAtHigh: hasShares ? periodHigh * shares : null,
        mcapAtLow: hasShares ? periodLow * shares : null,
        mcapAtLast: hasShares ? last * shares : null,
        highLabel: rangeHighLabel(period),
        lowLabel: rangeLowLabel(period),
        rangeBias: rangeBias(rangePosPct),
    };
}
